import { useTranslation } from 'react-i18next';
import {
  Modal,
  Input,
  Button,
  Text,
  Select,
  TextArea,
  Switch,
  FormRow
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import {
  GovOsResult,
  ManagedOnlineService,
  Verfahren
} from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache
} from '../api/govos-api';
import { removeEmptyValues } from '../utils/objectUtils';

interface ElementOnlinedienstMetadatenEditModalProps {
  onlinedienst: ManagedOnlineService;
  onClose: () => void;
  isOpen: boolean;
}

const antragsTypeOptions = [
  { label: 'Antrag', value: 'Antrag' },
  { label: 'Anliegen', value: 'Anliegen' },
  { label: 'Verfahren', value: 'Verfahren' }
];

type VerfahrenFormData = {
  processId?: string;
  title?: string;
  description?: string;
  antragsTyp?: string;
  prefillUpload?: boolean;
  encryptionActivated?: boolean;
  summary?: string;
  finish?: string;
  hybridSubmissionText?: string;
};

const getDefaultValuesFromExistingElement = (
  verfahren: Verfahren
): VerfahrenFormData => ({
  processId: String(verfahren.processId) || '',
  title: verfahren.metadata.title || '',
  description: verfahren.metadata.description || '',
  antragsTyp: verfahren.metadata.antragsTyp || '',
  prefillUpload: verfahren.metadata.isPrefillAvailable || false,
  encryptionActivated: verfahren.metadata.encryption?.enabled || false,
  summary: verfahren.metadata.summary || '',
  finish: verfahren.metadata.finalText || '',
  hybridSubmissionText: verfahren.metadata.hybridSubmissionText || ''
});

function EditOnlinedienstMetadatenModal({
  onlinedienst,
  onClose,
  isOpen
}: ElementOnlinedienstMetadatenEditModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);

  const { register, handleSubmit, formState, setValue, control } =
    useForm<VerfahrenFormData>({
      defaultValues: getDefaultValuesFromExistingElement(
        onlinedienst.editableContainer
      ),
      resolver: joiResolver(
        CustomJoi.object<VerfahrenFormData>({
          description: CustomJoi.string().optional().allow(''),
          title: CustomJoi.string().required(),
          antragsTyp: CustomJoi.string().optional().allow(''),
          prefillUpload: CustomJoi.boolean().optional(),
          encryptionActivated: CustomJoi.boolean().optional(),
          summary: CustomJoi.string().trim().optional().allow(''),
          finish: CustomJoi.string().trim().optional().allow(''),
          hybridSubmissionText: CustomJoi.string().trim().optional().allow(''),
          processId: CustomJoi.string().required()
        })
      ),
      mode: 'onBlur'
    });

  const { mutate: updateOnlinedienst } = useMutation({
    mutationFn: async (data: VerfahrenFormData) =>
      notifyUserFromRequest(
        () =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.configuredOnlineDienst(
              onlinedienst.editableContainer.processId
            ),
            removeEmptyValues(data)
          ),
        {
          successMessage: t('api.successMessage.configuredOnlineDienst'),
          errorMessage: t('api.errorMessage.configuredOnlineDienst'),
          loadingMessage: t('api.loadingMessage.configuredOnlineDienst')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateConfiguredOnlinediensteCache(
          onlinedienst.processId ?? ''
        )
      });
      onClose();
    }
  });

  const handleSave = handleSubmit((data) => {
    updateOnlinedienst(data);
  });

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('ElementOnlinedienstMetadatenEditModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <FormRow
            label={t('ElementOnlinedienstMetadatenEditModal.fields.kennung')}
          >
            <Input
              {...register('processId')}
              disabled
              style={{ width: '100%' }}
            />
          </FormRow>
          <FormRow
            label={t('ElementOnlinedienstMetadatenEditModal.fields.titel')}
            variant="error"
            validationMessage={formState.errors.title?.message}
          >
            <Input {...register('title')} style={{ width: '100%' }} />
          </FormRow>
          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.beschreibung'
            )}
            validationMessage={formState.errors.description?.message}
          >
            <TextArea
              {...register('description')}
              rows={4}
              style={{ width: '100%' }}
            />
          </FormRow>

          <FormRow
            label={t('ElementOnlinedienstMetadatenEditModal.fields.typ')}
            validationMessage={formState.errors.antragsTyp?.message}
          >
            <Controller
              name="antragsTyp"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || ''}
                  onChange={(e) => setValue('antragsTyp', e.at(0)?.value)}
                  onBlur={field.onBlur}
                  name={field.name}
                  options={antragsTypeOptions}
                  style={{ width: '100%' }}
                  placeholder={t('select.placeholder')}
                  defaultOptions={[
                    {
                      label: onlinedienst.editableContainer.metadata.antragsTyp,
                      value: onlinedienst.editableContainer.metadata.antragsTyp
                    }
                  ]}
                />
              )}
            />
          </FormRow>

          <FormRow label={t('ManagedOnlinedienstDetail.verfahren.templateId')}>
            <Input
              value={onlinedienst.editableContainer.templateId || '-'}
              disabled
              style={{ width: '100%' }}
            />
          </FormRow>

          <FormRow
            label={t('ManagedOnlinedienstDetail.verfahren.templateVersion')}
          >
            <Input
              value={onlinedienst.editableContainer.templateVersion || '-'}
              disabled
              style={{ width: '100%' }}
            />
          </FormRow>

          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.verschluesselung'
            )}
            validationMessage={formState.errors.encryptionActivated?.message}
          >
            <Switch
              {...register('encryptionActivated')}
              defaultChecked={
                onlinedienst.editableContainer.metadata.encryption.enabled
              }
              onChange={(checked) => {
                setValue('encryptionActivated', checked);
              }}
              label={t(
                'ElementOnlinedienstMetadatenEditModal.fields.verschluesselungActive'
              )}
            />
          </FormRow>

          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.vorbefuellung'
            )}
            validationMessage={formState.errors.prefillUpload?.message}
          >
            <Switch
              {...register('prefillUpload')}
              defaultChecked={
                onlinedienst.editableContainer.metadata.isPrefillAvailable
              }
              onChange={(checked) => {
                setValue('prefillUpload', checked);
              }}
              label={t(
                'ElementOnlinedienstMetadatenEditModal.fields.vorbefuellungActive'
              )}
            />
          </FormRow>

          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.zusammenfassung'
            )}
            validationMessage={formState.errors.summary?.message}
          >
            <TextArea {...register('summary')} rows={4} />
          </FormRow>

          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.textHybrideinreichung'
            )}
            validationMessage={formState.errors.hybridSubmissionText?.message}
          >
            <TextArea {...register('hybridSubmissionText')} rows={4} />
          </FormRow>

          <FormRow
            label={t(
              'ElementOnlinedienstMetadatenEditModal.fields.abschlusstext'
            )}
            validationMessage={formState.errors.finish?.message}
          >
            <TextArea {...register('finish')} rows={4} />
          </FormRow>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={handleCancel}
            style={{ flex: 1 }}
          >
            {t('ElementOnlinedienstMetadatenEditModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!formState.isValid}
            style={{ flex: 1 }}
          >
            {t('ElementOnlinedienstMetadatenEditModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export { EditOnlinedienstMetadatenModal };
